<template>
    <div class="ui-page">
        <ui-page-header :title="info ? info.name : 'Importação'" :subtitle="subtitle" :back="backRoute" back-text="QGC do caso">
            <v-btn v-if="info && !info.deleted_at" outlined color="error" :disabled="Number(info.status) !== 1 || !$check('Creditors', 'DELETE')" @click="removeDialog = true">
                <v-icon left small>mdi-delete-outline</v-icon>
                Remover lista
            </v-btn>
            <v-btn outlined :disabled="!info || !info.has_file" :loading="downloading" @click="downloadFile">
                <v-icon left small>mdi-file-download-outline</v-icon>
                Baixar planilha original
            </v-btn>
        </ui-page-header>

        <v-alert v-if="info && info.processing_error" type="error" text dense class="mb-4">
            Erro no processamento: {{ info.processing_error }}
        </v-alert>
        <v-alert v-if="info && info.deleted_at" type="warning" text dense class="mb-4">
            Esta lista foi removida em {{ info.deleted_at | defaultDate("DD/MM/YYYY HH:mm") }}; os créditos dela foram removidos ou restaurados.
        </v-alert>

        <creditor-list-remove-dialog v-model="removeDialog" :list="info ? { id: info.id, name: info.name } : null" @removed="onRemoved" />

        <section v-if="info" class="import-stats mb-5" aria-label="Resumo da importação">
            <div class="import-stat">
                <span class="import-stat__label">Situação</span>
                <ui-badge :text="statusLabel(info.status)" :color="statusColor(info.status)" class="mt-1" />
                <span class="import-stat__hint">enviada em {{ info.created_at | defaultDate("DD/MM/YYYY HH:mm") }}</span>
                <span v-if="info.finished_at" class="import-stat__hint">concluída em {{ info.finished_at | defaultDate("DD/MM/YYYY HH:mm") }}</span>
            </div>
            <div class="import-stat">
                <span class="import-stat__label">Linhas processadas</span>
                <strong>{{ info.current_line }}<small> / {{ info.total_lines }}</small></strong>
                <span class="import-stat__hint">linhas da planilha</span>
            </div>
            <div class="import-stat">
                <span class="import-stat__label">Créditos criados</span>
                <strong>{{ info.summary.created }}</strong>
                <span class="import-stat__hint">novos no QGC</span>
            </div>
            <div class="import-stat">
                <span class="import-stat__label">Créditos atualizados</span>
                <strong>{{ info.summary.updated }}</strong>
                <span class="import-stat__hint">já existiam (mesmo CPF/CNPJ, classe e moeda)</span>
            </div>
            <div class="import-stat">
                <span class="import-stat__label">Desfeitos</span>
                <strong>{{ info.summary.reverted }}</strong>
                <span class="import-stat__hint">pela remoção da lista</span>
            </div>
        </section>

        <v-alert v-if="info && !info.tracked" type="info" text class="mb-4">
            Esta importação é anterior ao registro linha a linha. Para ver o que foi subido, baixe a planilha original.
        </v-alert>

        <template v-if="info && info.tracked">
            <div class="ui-list-bar">
                <ui-pills v-model="operation" :items="operationPills" @input="applyFilters" />
            </div>

            <ui-table-card clearable :can-clear="!!search || operation !== ''" @clear="clearFilters">
                <template #toolbar>
                    <v-text-field v-model="search" placeholder="Buscar por nome ou CPF/CNPJ" prepend-inner-icon="mdi-magnify" outlined dense hide-details
                        class="ui-toolbar__search" @input="searchDebounced" @keyup.enter="applyFilters" />
                </template>
                <v-data-table :headers="headers" :items="items" :loading="loading" :server-items-length="total" disable-sort hide-default-footer
                    no-data-text="Nenhuma linha encontrada" loading-text="Carregando linhas...">
                    <template v-slot:[`item.row_number`]="{ item }">
                        <span class="ui-muted">{{ item.row_number || "—" }}</span>
                    </template>
                    <template v-slot:[`item.name`]="{ item }">
                        <div class="ui-cell-main">{{ field(item, "name") || "—" }}</div>
                        <div class="ui-cell-sub">{{ formatRegistration(field(item, "registration")) }}<span v-if="field(item, 'email')"> · {{ field(item, "email") }}</span></div>
                    </template>
                    <template v-slot:[`item.type`]="{ item }">{{ typeLabel(field(item, "type")) }}</template>
                    <template v-slot:[`item.amount`]="{ item }">
                        <span class="ui-nowrap">{{ formatAmount(field(item, "amount"), field(item, "currency")) }}</span>
                        <div v-if="item.operation === 'updated' && item.before && Number(item.before.amount) !== Number(field(item, 'amount'))" class="ui-cell-sub">
                            antes {{ formatAmount(item.before.amount, item.before.currency) }}
                        </div>
                    </template>
                    <template v-slot:[`item.operation`]="{ item }">
                        <ui-badge :text="item.operation === 'updated' ? 'Atualizado' : 'Criado'" :tone="item.operation === 'updated' ? 'info' : 'success'" />
                        <div v-if="item.reverted_at" class="ui-cell-sub import-reverted">desfeito em {{ item.reverted_at | defaultDate("DD/MM/YYYY") }}</div>
                        <div v-else-if="!item.active" class="ui-cell-sub import-reverted">crédito removido do QGC</div>
                    </template>
                </v-data-table>
                <template #footer>
                    <ui-pagination :total="total" :page="page" :items-per-page="itemsPerPage" :disabled="loading"
                        @update:page="changePage" @update:itemsPerPage="changePageSize" />
                </template>
            </ui-table-card>
        </template>
    </div>
</template>

<script>
import axios from "axios";
import _ from "lodash";
import creditorClassUtil from "@/common/util/creditor-class.util";
import creditorListStatus from "@/common/data/creditor-list-status.data";
import CreditorListRemoveDialog from "@/components/CreditorListRemoveDialog.vue";

// Lista de credores: o que uma importacao subiu (linha da planilha, credor,
// classe, valor e se criou ou atualizou o credito), com a planilha original.
export default {
    name: "CreditorsListImport",

    components: { CreditorListRemoveDialog },

    data: () => ({
        info: null,
        // Classes do caso (inclui as personalizadas e as ocultas) para o rotulo da coluna.
        classes: [],
        items: [],
        total: 0,
        page: 1,
        itemsPerPage: 25,
        search: "",
        operation: "",
        loading: false,
        downloading: false,
        removeDialog: false,
        headers: [
            { text: "Linha", value: "row_number", width: 80 },
            { text: "Credor", value: "name" },
            { text: "Classe", value: "type" },
            { text: "Valor", value: "amount" },
            { text: "Operação", value: "operation" },
        ],
    }),

    computed: {
        // Volta para o QGC do caso (onde ficam as importacoes).
        backRoute() {
            return this.info && this.info.client_id ? { path: "/qgc", query: { client_id: this.info.client_id } } : "/qgc";
        },

        subtitle() {
            if (!this.info) return "";
            const parts = [this.info.client_name || "Caso não informado"];
            if (this.info.client_company_name) parts.push(this.info.client_company_name);
            parts.push(this.versionLabel(this.info.version));
            return parts.join(" · ");
        },

        operationPills() {
            const summary = (this.info && this.info.summary) || {};
            return [
                { value: "", text: `Todas (${summary.items || 0})` },
                { value: "created", text: `Criados (${summary.created || 0})`, tone: "success" },
                { value: "updated", text: `Atualizados (${summary.updated || 0})`, tone: "info" },
                { value: "reverted", text: `Desfeitos (${summary.reverted || 0})`, tone: "neutral" },
            ];
        },
    },

    created() {
        this.loadInfo();
        this.loadItems();
    },

    methods: {
        notifyError(error) {
            this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 5000 });
        },

        async loadInfo() {
            try {
                const response = await axios.get(`/api/admin/creditor-lists/${this.$route.params.id}`);
                this.info = response.data.content;
                this.classes = await creditorClassUtil.listLabels(this.info.client_id);
            } catch (error) {
                this.notifyError(error);
            }
        },

        async loadItems() {
            this.loading = true;
            try {
                const response = await axios.get(`/api/admin/creditor-lists/${this.$route.params.id}/items`, {
                    params: { page: this.page, limit: this.itemsPerPage, search: this.search || undefined, operation: this.operation || undefined },
                });
                const content = response.data.content;
                this.items = content.data;
                this.total = content.paging.total;
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.loading = false;
            }
        },

        applyFilters() {
            this.page = 1;
            this.loadItems();
        },

        searchDebounced: _.debounce(function () {
            this.applyFilters();
        }, 400),

        clearFilters() {
            this.search = "";
            this.operation = "";
            this.applyFilters();
        },

        changePage(page) {
            this.page = page;
            this.loadItems();
        },

        changePageSize(size) {
            this.itemsPerPage = size;
            this.applyFilters();
        },

        onRemoved() {
            this.$router.push(this.backRoute);
        },

        async downloadFile() {
            this.downloading = true;
            try {
                const response = await axios.get(`/api/admin/creditor-lists/${this.$route.params.id}/file`);
                window.open(response.data.content.url, "_blank");
            } catch (error) {
                this.notifyError(error);
            } finally {
                this.downloading = false;
            }
        },

        field(item, key) {
            return item.imported ? item.imported[key] : null;
        },

        typeLabel(type) {
            const found = this.classes.find((item) => Number(item.id) === Number(type));
            return found ? found.name : "—";
        },

        versionLabel(version) {
            return ({ 1: "Edital Art. 52", 2: "Edital Art. 7", 3: "QGC Consolidado" })[Number(version)] || `QGC versão ${version}`;
        },

        statusLabel(status) {
            const found = creditorListStatus.find((item) => Number(item.id) === Number(status));
            return found ? found.name : "—";
        },

        statusColor(status) {
            return ({ 0: "warning", 1: "accent", 2: "info", 3: "error" })[Number(status)] || "grey";
        },

        formatRegistration(value) {
            const digits = String(value || "").replace(/\D/g, "");
            if (digits.length === 11) return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
            if (digits.length === 14) return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, "$1.$2.$3/$4-$5");
            return value || "Sem CPF/CNPJ";
        },

        formatAmount(value, currency) {
            if (value === null || value === undefined || value === "") return "—";
            const code = ({ 1: "BRL", 2: "USD", 3: "EUR" })[Number(currency)] || "BRL";
            try {
                return Number(value).toLocaleString("pt-BR", { style: "currency", currency: code });
            } catch (error) {
                return Number(value).toLocaleString("pt-BR", { minimumFractionDigits: 2 });
            }
        },
    },
};
</script>

<style scoped>
.import-stats {
    display: grid;
    grid-template-columns: 1.2fr repeat(4, minmax(0, 1fr));
    gap: 12px;
}

.import-stat {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding: 12px 14px;
    border: 1px solid #e6e2ee;
    border-radius: 10px;
    background: #fff;
}

.import-stat__label {
    font-size: 12.5px;
    color: #6b6880;
}

.import-stat strong {
    font-size: 22px;
    font-weight: 600;
    color: #1d0533;
}

.import-stat strong small {
    font-size: 14px;
    color: #6b6880;
}

.import-stat__hint {
    font-size: 12px;
    color: #6b6880;
}

.import-reverted {
    color: #b45309;
}

@media (max-width: 1100px) {
    .import-stats {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}
</style>
